import { computed, signal } from "@preact/signals-react"
import { IFinancialAccount, IProfile } from "@/app/dashboard/models/User"
import {
  createFinancialAccount,
  getFinancialDetails,
  getProfile,
  updateProfile
} from "@/services/user-service"
import { showCommonApiError, showSuccess } from "@/lib/toast-utill"
import { userDetails } from "@/store/user.store"

export const profile = signal<IProfile>()
export const financialAccount = signal<Partial<IFinancialAccount>>()
export const croppedImage = signal<string | null>(null)
export const croppedFile = signal<File | null>(null)
export const selectedFile = signal<File | null>(null)

export enum FinancialAccountStatus {
  Default = "Default",
  AccountNotAvailable = "AccountNotAvailable",
  VerificationNeeded = "VerificationNeeded",
  PayoutNotEnabled = "PayoutNotEnabled",
  PayoutEnabled = "PayoutEnabled"
}

const financialAccountStatusTextMap = {
  [FinancialAccountStatus.Default]: "",
  [FinancialAccountStatus.AccountNotAvailable]: "Financial account not available",
  [FinancialAccountStatus.PayoutNotEnabled]: "Payout not enabled",
  [FinancialAccountStatus.PayoutEnabled]: "Payouts enabled",
  [FinancialAccountStatus.VerificationNeeded]: "Verification Needed"
}

export const financialAccountStatus = computed(() => {
  const account = financialAccount.value
  if (!account) {
    return FinancialAccountStatus.Default
  }
  if (!account.isCreated) {
    return FinancialAccountStatus.AccountNotAvailable
  }
  if (!account.isPayoutsEnabled) {
    return FinancialAccountStatus.PayoutNotEnabled
  }
  if (account.isVerificationNeeded) {
    return FinancialAccountStatus.VerificationNeeded
  }
  if (account.isPayoutsEnabled) {
    return FinancialAccountStatus.PayoutEnabled
  }
})

export const financialAccountStatusText = computed(
  () => financialAccountStatusTextMap[financialAccountStatus.value] ?? FinancialAccountStatus.Default
)

export const loadProfileData = async () => {
  const [profileResponse, error] = await getProfile()
  if (error) {
    showCommonApiError("getting profile")
    return
  }
  profile.value = profileResponse
  userDetails.value = profileResponse
}

export const patchProfileValues = (p: Partial<IProfile>) => {
  profile.value = { ...profile.value, ...p }
}

export const submitProfileData = async () => {
  if (croppedImage.value) {
    patchProfileValues({ image: croppedImage.value })
  }
  if (croppedFile.value) {
    console.log("croppedFile", croppedFile.value);
    patchProfileValues({ imageFile: croppedFile.value })
  }

  const rawDateOfBirth = profile.value.dateOfBirth
  const dateOfBirthAsDate = rawDateOfBirth ? new Date(rawDateOfBirth) : null
  const payload = {
    ...profile.value,
    dateOfBirth:
      dateOfBirthAsDate && !isNaN(dateOfBirthAsDate.getTime())
        ? dateOfBirthAsDate.toISOString()
        : undefined
  }
  const [profileResponse, error] = await updateProfile(payload)
  if (error) {
    showCommonApiError("updating profile")
    return
  }
  loadProfileData()
  selectedFile.value = null
  showSuccess("Profile successfully updated", "Saved")
}

export const loadFinancialDetails = async () => {
  const [financialAccountResponse, error] = await getFinancialDetails()
  financialAccount.value = financialAccountResponse ?? { isCreated: false }
}

export const createNewFinancialAccount = async () => {
  const [financialAccountResponse, error] = await createFinancialAccount()
  if (error) {
    return showCommonApiError("getting financial account")
  }
  financialAccount.value = financialAccountResponse ?? { isCreated: false }
  showSuccess("Financial account created.", "Created")
}
